
import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import SpeakingBookingForm from './components/SpeakingBookingForm';

export default function Speaking() {
  const [showBookingForm, setShowBookingForm] = useState(false);

  // Check for payment success/cancel from URL params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const canceled = urlParams.get('canceled');
    
    if (success) {
      alert('Payment successful! Your speaking engagement request has been submitted. We will contact you within 24 hours.');
      // Clear URL params
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (canceled) {
      alert('Payment was cancelled. Your request was not submitted.');
      // Clear URL params
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleBookingSubmit = (formData: any) => {
    // Form submission is handled in SpeakingBookingForm component
    setShowBookingForm(false);
  };

  const [formData, setFormData] = useState({
    organizationName: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    eventType: '',
    eventDate: '',
    audienceSize: '',
    eventDuration: '',
    topicPreference: '',
    budgetRange: '',
    location: '',
    format: '',
    specialRequirements: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-speaking-engagement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitStatus('success');
        setFormData({
          organizationName: '',
          contactName: '',
          contactEmail: '',
          contactPhone: '',
          eventType: '',
          eventDate: '',
          audienceSize: '',
          eventDuration: '',
          topicPreference: '',
          budgetRange: '',
          location: '',
          format: '',
          specialRequirements: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const speakingTopics = [
    {
      title: 'The Dream Journal Method',
      description: 'Discover how to capture divine inspiration and turn dreams into actionable plans.',
      icon: 'ri-book-open-line',
      duration: '45-60 minutes'
    },
    {
      title: 'Faith-Based Leadership',
      description: 'Leading with authenticity, purpose, and spiritual wisdom in any organization.',
      icon: 'ri-team-line',
      duration: '30-90 minutes'
    },
    {
      title: 'Purpose-Driven Success',
      description: 'Aligning personal and professional goals with your divine calling.',
      icon: 'ri-compass-line',
      duration: '45-75 minutes'
    },
    {
      title: 'Transformational Journaling',
      description: 'Practical techniques for personal growth and spiritual development.',
      icon: 'ri-edit-line',
      duration: '30-60 minutes'
    }
  ];

  const eventTypes = [
    'Corporate Conference',
    'Church Event',
    'Leadership Summit',
    'Workshop/Seminar',
    'Retreat',
    'Graduation/Commencement',
    'Fundraising Event',
    'Team Building',
    'Other'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative py-20 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(30, 58, 138, 0.8), rgba(30, 58, 138, 0.8)), url('https://public.readdy.ai/ai/img_res/ccf57615db130356acbfa4bc2d7b0d4e.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-5xl font-bold mb-6">Speaking Engagements</h1>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Inspire your audience with transformational messages on faith, leadership, 
            and purpose-driven success. Available for corporate events, conferences, and spiritual gatherings.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white text-white hover:bg-white hover:text-slate-800"
            >
              Download Speaker Kit
            </Button>
          </div>
        </div>
      </section>

      {/* New Request Speaking Engagement Button */}
      <div className="text-center my-12">
        <Button 
          onClick={() => setShowBookingForm(true)}
          size="lg" 
          className="bg-blue-800 text-white hover:bg-blue-900"
        >
          <i className="ri-calendar-check-line mr-2"></i>
          Request Speaking Engagement
        </Button>
      </div>

      {showBookingForm && (
        <SpeakingBookingForm
          onSubmit={handleBookingSubmit}
          onClose={() => setShowBookingForm(false)}
        />
      )}

      {/* Speaking Topics */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-800 mb-4">Popular Speaking Topics</h2>
            <p className="text-xl text-slate-600">Customizable presentations tailored to your audience and objectives</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {speakingTopics.map((topic, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <i className={`${topic.icon} text-2xl text-blue-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-3">{topic.title}</h3>
                <p className="text-slate-600 mb-4">{topic.description}</p>
                <div className="flex items-center text-sm text-slate-500">
                  <i className="ri-time-line mr-2"></i>
                  <span>{topic.duration}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Speaking Experience */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-slate-800 mb-6">Experienced Speaker</h2>
              <p className="text-lg text-slate-600 mb-6">
                With years of experience speaking to diverse audiences, I bring authentic, 
                engaging presentations that inspire action and transformation.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-check-line text-green-600"></i>
                  </div>
                  <span className="text-slate-700">500+ speaking engagements delivered</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-check-line text-green-600"></i>
                  </div>
                  <span className="text-slate-700">Corporate and faith-based audiences</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-green-200 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-check-line text-green-600"></i>
                  </div>
                  <span className="text-slate-700">Customized content for every event</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-green-200 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-check-line text-green-600"></i>
                  </div>
                  <span className="text-slate-700">Virtual and in-person presentations</span>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=professional%20speaker%20presenting%20to%20engaged%20corporate%20audience%2C%20confident%20presenter%20on%20stage%2C%20business%20conference%20setting%2C%20inspirational%20leadership%20moment&width=600&height=500&seq=speaker1&orientation=landscape"
                alt="Speaking engagement"
                className="rounded-lg shadow-lg object-cover object-top w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-3xl font-bold text-slate-800 mb-6 text-center">Request Speaking Engagement</h2>
            
            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800">Thank you for your speaking request! I'll contact you within 24 hours to discuss details and availability.</p>
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800">There was an error submitting your request. Please try again.</p>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-slate-800 mb-4">Organization Details</h4>
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="organizationName" className="block text-sm font-medium text-slate-700 mb-2">
                          Organization Name *
                        </label>
                        <input
                          type="text"
                          id="organizationName"
                          name="organizationName"
                          value={formData.organizationName}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          placeholder="Your organization name"
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="contactName" className="block text-sm font-medium text-slate-700 mb-2">
                            Contact Name *
                          </label>
                          <input
                            type="text"
                            id="contactName"
                            name="contactName"
                            value={formData.contactName}
                            onChange={handleInputChange}
                            required
                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            placeholder="Your name"
                          />
                        </div>
                        <div>
                          <label htmlFor="contactEmail" className="block text-sm font-medium text-slate-700 mb-2">
                            Email Address *
                          </label>
                          <input
                            type="email"
                            id="contactEmail"
                            name="contactEmail"
                            value={formData.contactEmail}
                            onChange={handleInputChange}
                            required
                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            placeholder="contact@organization.com"
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="contactPhone" className="block text-sm font-medium text-slate-700 mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          id="contactPhone"
                          name="contactPhone"
                          value={formData.contactPhone}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          placeholder="(555) 123-4567"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-slate-800 mb-4">Event Details</h4>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="eventType" className="block text-sm font-medium text-slate-700 mb-2">
                            Event Type *
                          </label>
                          <select
                            id="eventType"
                            name="eventType"
                            value={formData.eventType}
                            onChange={handleInputChange}
                            required
                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm pr-8"
                          >
                            <option value="">Select event type</option>
                            {eventTypes.map((type) => (
                              <option key={type} value={type}>{type}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label htmlFor="eventDate" className="block text-sm font-medium text-slate-700 mb-2">
                            Preferred Date
                          </label>
                          <input
                            type="date"
                            id="eventDate"
                            name="eventDate"
                            value={formData.eventDate}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="audienceSize" className="block text-sm font-medium text-slate-700 mb-2">
                            Expected Audience Size
                          </label>
                          <select
                            id="audienceSize"
                            name="audienceSize"
                            value={formData.audienceSize}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm pr-8"
                          >
                            <option value="">Select audience size</option>
                            <option value="1-25">1-25 people</option>
                            <option value="26-50">26-50 people</option>
                            <option value="51-100">51-100 people</option>
                            <option value="101-250">101-250 people</option>
                            <option value="251-500">251-500 people</option>
                            <option value="500+">500+ people</option>
                          </select>
                        </div>
                        <div>
                          <label htmlFor="eventDuration" className="block text-sm font-medium text-slate-700 mb-2">
                            Presentation Duration
                          </label>
                          <select
                            id="eventDuration"
                            name="eventDuration"
                            value={formData.eventDuration}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm pr-8"
                          >
                            <option value="">Select duration</option>
                            <option value="30 minutes">30 minutes</option>
                            <option value="45 minutes">45 minutes</option>
                            <option value="60 minutes">60 minutes</option>
                            <option value="90 minutes">90 minutes</option>
                            <option value="Half day">Half day</option>
                            <option value="Full day">Full day</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-slate-800 mb-4">Preferences</h4>
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="format" className="block text-sm font-medium text-slate-700 mb-2">
                          Presentation Format
                        </label>
                        <select
                          id="format"
                          name="format"
                          value={formData.format}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm pr-8"
                        >
                          <option value="">Select format</option>
                          <option value="in-person">In-Person</option>
                          <option value="virtual">Virtual</option>
                          <option value="hybrid">Hybrid</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="location" className="block text-sm font-medium text-slate-700 mb-2">
                          Event Location
                        </label>
                        <input
                          type="text"
                          id="location"
                          name="location"
                          value={formData.location}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          placeholder="City, State or venue address"
                        />
                      </div>
                      <div>
                        <label htmlFor="budgetRange" className="block text-sm font-medium text-slate-700 mb-2">
                          Budget Range
                        </label>
                        <select
                          id="budgetRange"
                          name="budgetRange"
                          value={formData.budgetRange}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm pr-8"
                        >
                          <option value="">Select budget range</option>
                          <option value="Under $2,500">Under $2,500</option>
                          <option value="$2,500 - $5,000">$2,500 - $5,000</option>
                          <option value="$5,000 - $10,000">$5,000 - $10,000</option>
                          <option value="$10,000 - $25,000">$10,000 - $25,000</option>
                          <option value="Over $25,000">Over $25,000</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="topicPreference" className="block text-sm font-medium text-slate-700 mb-2">
                          Topic Preference
                        </label>
                        <textarea
                          id="topicPreference"
                          name="topicPreference"
                          value={formData.topicPreference}
                          onChange={handleInputChange}
                          rows={3}
                          maxLength={300}
                          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                          placeholder="Which topics interest you most? Any specific themes or messages?"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          {formData.topicPreference.length}/300 characters
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-slate-800 mb-4">Additional Information</h4>
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="specialRequirements" className="block text-sm font-medium text-slate-700 mb-2">
                          Special Requirements
                        </label>
                        <textarea
                          id="specialRequirements"
                          name="specialRequirements"
                          value={formData.specialRequirements}
                          onChange={handleInputChange}
                          rows={3}
                          maxLength={300}
                          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                          placeholder="AV requirements, accessibility needs, travel arrangements, etc."
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          {formData.specialRequirements.length}/300 characters
                        </p>
                      </div>
                      <div>
                        <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                          Additional Message
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          rows={4}
                          maxLength={500}
                          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                          placeholder="Tell us more about your event, audience, and objectives (max 500 characters)"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          {formData.message.length}/500 characters
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-slate-200">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start">
                    <i className="ri-information-line text-blue-600 text-lg mr-3 mt-0.5"></i>
                    <div>
                      <h4 className="font-semibold text-blue-800 mb-1">Next Steps</h4>
                      <p className="text-blue-700 text-sm">
                        After submitting this request, I'll contact you within 24 hours to discuss your event details, 
                        confirm availability, and provide a customized proposal with pricing.
                      </p>
                    </div>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-blue-800 hover:bg-blue-900" 
                  disabled={isSubmitting}
                  size="lg"
                >
                  {isSubmitting ? 'Submitting Request...' : 'Submit Speaking Request'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-800 mb-12 text-center">What Event Organizers Say</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: "Jennifer Martinez",
                role: "Conference Director",
                organization: "Leadership Summit 2024",
                text: "Elbert's presentation on faith-based leadership was the highlight of our conference. Attendees are still talking about it months later.",
                rating: 5
              },
              {
                name: "David Thompson",
                role: "HR Director",
                organization: "TechCorp Industries",
                text: "His message on purpose-driven success resonated perfectly with our team. Professional, engaging, and truly transformational.",
                rating: 5
              },
              {
                name: "Pastor Sarah Williams",
                role: "Senior Pastor",
                organization: "Grace Community Church",
                text: "Elbert connected with our congregation beautifully. His authentic approach and practical wisdom made a lasting impact.",
                rating: 5
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-lg">
                <div className="flex text-yellow-400 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill"></i>
                  ))}
                </div>
                <p className="text-slate-600 mb-4 italic">"{testimonial.text}"</p>
                <div>
                  <div className="font-semibold text-slate-800">{testimonial.name}</div>
                  <div className="text-sm text-slate-500">{testimonial.role}</div>
                  <div className="text-sm text-blue-600">{testimonial.organization}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
